import React from "react";

import { ParticipantStatus, ParticipantTypes, UserStatus } from "@api/graphql";
import { useIsAuthorisedUser } from "@components/permission_control/ProtectedComponent";
import { Role } from "@roles";

interface AuthorisationRequirements {
  requiredGroups?: Role[];
  requiredUserStatus?: UserStatus;
  requiredParticipantStatus?: ParticipantStatus;
  requiredParticipantTypes?: ParticipantTypes[];
}
const isDisableableElement = (element: React.ReactElement): element is React.ReactElement<{ disabled: boolean }> => {
  const disableableTypes = ["button", "input", "select", "textarea"];

  if (disableableTypes.includes(element.type as string)) {
    return true;
  }

  if (typeof element.type === "object" && ("displayName" in element.type || "name" in element.type)) {
    const componentName = (element.type as { displayName?: string }).displayName;
    const disableableComponents = ["FormControl"];
    return componentName ? disableableComponents.includes(componentName) : false;
  }
  return false;
};

const disableChild = (child: React.ReactNode) => {
  if (React.isValidElement(child) && isDisableableElement(child)) {
    return React.cloneElement(child, { disabled: true });
  }
  return child;
};

export const EnableForUser = ({
  children,
  disableOnUnauth,
  ...authorisationRequirements
}: {
  children: React.ReactNode;
  disableOnUnauth?: boolean;
} & AuthorisationRequirements) => {
  const { requiredGroups, requiredUserStatus, requiredParticipantStatus, requiredParticipantTypes } =
    authorisationRequirements;

  // Use the hook to check if the user is authorized
  const isAuthorisedUser = useIsAuthorisedUser({
    requiredGroups,
    requiredUserStatus,
    requiredParticipantStatus,
    requiredParticipantTypes,
  });

  // Disable the children if the user is NOT authorized and `disableOnUnauth` is true
  if (disableOnUnauth && !isAuthorisedUser) {
    return <>{React.Children.map(children, disableChild)}</>;
  }

  return <>{children}</>;
};
